// src/components/layout/Topbar.jsx
// Fully responsive — hamburger menu + slide-out drawer
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUIStore } from '../../store/uiStore';
import { useAuth }   from '../../hooks/useAuth';
import AuthModal     from '../auth/AuthModal';
import { C }         from '../../styles/tokens';

const navLinks = [
  { label: 'Opportunities', path: '/opportunities', public: true,  icon: '🌐' },
  { label: 'Training',      path: '/training',      public: false, icon: '🎓' },
  { label: 'Collaborators', path: '/collaborators', public: true,  icon: '🏢' },
];

export default function Topbar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, profile, logout } = useAuth();
  const { authModal, openAuthModal, closeAuthModal } = useUIStore();
  const [scrolled,   setScrolled]   = useState(false);
  const [dropOpen,   setDropOpen]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isActive = (path) => location.pathname === path;
  const roleColor = { admin: C.magenta, collaborator: C.info, talent: C.success };

  const handleNavClick = (link) => {
    if (!link.public && !user) { openAuthModal('login'); return; }
    navigate(link.path);
  };

  return (
    <>
      <style>{`
        @media (min-width: 769px) {
          .topbar-nav-desktop   { display: flex !important; }
          .topbar-auth-desktop  { display: flex !important; }
          .topbar-hamburger     { display: none  !important; }
        }
        @media (max-width: 768px) {
          .topbar-nav-desktop   { display: none  !important; }
          .topbar-auth-desktop  { display: none  !important; }
          .topbar-hamburger     { display: flex  !important; }
        }
      `}</style>

      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 clamp(14px,4vw,28px)',
        background: scrolled ? 'rgba(4,4,15,0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(254,52,232,0.1)' : '1px solid transparent',
        transition: 'all 0.4s ease',
      }}>
        {/* Logo */}
        <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '9px', cursor: 'pointer', flexShrink: 0 }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', boxShadow: `0 0 14px ${C.magenta}50` }}>⬡</div>
          <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: '16px' }}>
            Oracle <span style={{ color: C.magenta }}>Nexus</span>
          </span>
        </div>

        {/* Desktop nav */}
        <div className="topbar-nav-desktop" style={{ gap: '4px', alignItems: 'center' }}>
          {navLinks.map(link => (
            <span key={link.path} onClick={() => handleNavClick(link)} style={{ padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s', color: isActive(link.path) ? '#fff' : 'rgba(255,255,255,0.55)', background: isActive(link.path) ? 'rgba(254,52,232,0.1)' : 'transparent', border: isActive(link.path) ? '1px solid rgba(254,52,232,0.2)' : '1px solid transparent' }}>
              {link.label}
            </span>
          ))}
          {profile?.role === 'admin' && (
            <span onClick={() => navigate('/admin')} style={{ padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', color: C.magenta, border: '1px solid rgba(254,52,232,0.2)', background: 'rgba(254,52,232,0.06)' }}>
              ⚡ Admin
            </span>
          )}
        </div>

        {/* Desktop auth */}
        <div className="topbar-auth-desktop" style={{ gap: '10px', alignItems: 'center' }}>
          {user ? (
            <div ref={dropRef} style={{ position: 'relative' }}>
              <div onClick={() => setDropOpen(d => !d)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '4px 10px 4px 4px', borderRadius: '10px', background: dropOpen ? 'rgba(254,52,232,0.1)' : 'rgba(255,255,255,0.05)', border: '1px solid rgba(254,52,232,0.15)', transition: 'all 0.2s' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>
                  {(profile?.displayName || user.email)?.charAt(0)?.toUpperCase()}
                </div>
                <span style={{ fontSize: '13px', fontWeight: 600, maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {profile?.displayName?.split(' ')[0] || 'Account'}
                </span>
                <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', transition: 'transform 0.2s', display: 'inline-block', transform: dropOpen ? 'rotate(180deg)' : 'none' }}>▼</span>
              </div>
              {dropOpen && (
                <div style={{ position: 'absolute', top: '46px', right: 0, width: '200px', background: '#0f0f24', border: '1px solid rgba(254,52,232,0.2)', borderRadius: '14px', padding: '8px', zIndex: 300, boxShadow: '0 16px 48px rgba(0,0,0,0.7)' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '8px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700 }}>{profile?.displayName || 'User'}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                    <div style={{ marginTop: '6px', fontSize: '10px', padding: '2px 8px', borderRadius: '100px', display: 'inline-flex', background: `${roleColor[profile?.role] || C.success}18`, color: roleColor[profile?.role] || C.success, fontWeight: 700, textTransform: 'uppercase' }}>{profile?.role || 'talent'}</div>
                  </div>
                  {[
                    { label: '📊 Dashboard',  path: '/dashboard' },
                    { label: '🪙 Wallet',      path: '/wallet' },
                    { label: '🎓 Training',    path: '/training' },
                    { label: '🎁 Referrals',   path: '/referral' },
                    ...(profile?.role === 'collaborator' ? [{ label: '🏢 My Portal', path: '/collaborators' }] : []),
                    ...(profile?.role === 'admin'        ? [{ label: '⚡ Admin',     path: '/admin' }] : []),
                  ].map(item => (
                    <div key={item.path} onClick={() => { navigate(item.path); setDropOpen(false); }} style={{ padding: '9px 12px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: 'rgba(255,255,255,0.75)', transition: 'all 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(254,52,232,0.08)'; e.currentTarget.style.color = '#fff'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}>
                      {item.label}
                    </div>
                  ))}
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '6px 0' }} />
                  <div onClick={async () => { await logout(); setDropOpen(false); }} style={{ padding: '9px 12px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: C.danger }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    🚪 Sign Out
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <button onClick={() => openAuthModal('login')} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid rgba(254,52,232,0.35)', borderRadius: '9px', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Log In</button>
              <button onClick={() => openAuthModal('register')} style={{ padding: '8px 18px', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, border: 'none', borderRadius: '9px', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: "'Syne',sans-serif" }}>Get Started →</button>
            </>
          )}
        </div>

        {/* Hamburger (mobile) */}
        <div className="topbar-hamburger" style={{ alignItems: 'center', gap: '10px' }}>
          {user && (
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700 }}>
              {(profile?.displayName || user.email)?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <button onClick={() => setMobileOpen(o => !o)} style={{ width: '40px', height: '40px', borderRadius: '9px', background: mobileOpen ? 'rgba(254,52,232,0.15)' : 'rgba(255,255,255,0.07)', border: '1px solid rgba(254,52,232,0.2)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexDirection: 'column', gap: '4px', padding: '11px', transition: 'all 0.2s' }} aria-label="Menu">
            {mobileOpen ? (
              <span style={{ fontSize: '15px' }}>✕</span>
            ) : (
              <>
                <div style={{ width: '16px', height: '2px', background: '#fff', borderRadius: '2px' }} />
                <div style={{ width: '11px', height: '2px', background: 'rgba(255,255,255,0.5)', borderRadius: '2px', alignSelf: 'flex-end' }} />
                <div style={{ width: '16px', height: '2px', background: '#fff', borderRadius: '2px' }} />
              </>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, top: '64px', background: 'rgba(4,4,15,0.75)', backdropFilter: 'blur(4px)', zIndex: 98 }} />
      )}

      {/* Mobile drawer */}
      <div style={{
        position: 'fixed', top: '64px', left: 0, bottom: 0, width: '285px',
        background: '#0a0a1a',
        borderRight: '1px solid rgba(254,52,232,0.12)',
        zIndex: 99,
        overflowY: 'auto',
        transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1)',
        display: 'flex', flexDirection: 'column',
      }}>
        {user ? (
          <>
            <div style={{ padding: '18px 16px', borderBottom: '1px solid rgba(254,52,232,0.08)', background: 'rgba(254,52,232,0.04)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '11px', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '19px', fontWeight: 700, flexShrink: 0 }}>
                  {(profile?.displayName || user.email)?.charAt(0)?.toUpperCase()}
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontSize: '15px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{profile?.displayName || 'User'}</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</div>
                </div>
              </div>
            </div>
            <nav style={{ padding: '10px 8px', flex: 1 }}>
              {[
                { label: '📊 Dashboard',     path: '/dashboard' },
                { label: '🌐 Opportunities', path: '/opportunities' },
                { label: '🎓 Training',      path: '/training' },
                { label: '🏢 Collaborators', path: '/collaborators' },
                { label: '🪙 Wallet',        path: '/wallet' },
                { label: '🎁 Referrals',     path: '/referral' },
                ...(profile?.role === 'admin' ? [{ label: '⚡ Admin Panel', path: '/admin' }] : []),
              ].map(item => (
                <div key={item.path} onClick={() => navigate(item.path)} style={{ display: 'flex', alignItems: 'center', padding: '13px 14px', borderRadius: '10px', fontSize: '15px', fontWeight: isActive(item.path) ? 700 : 500, cursor: 'pointer', marginBottom: '3px', color: isActive(item.path) ? '#fff' : 'rgba(255,255,255,0.65)', background: isActive(item.path) ? 'rgba(254,52,232,0.12)' : 'transparent', border: isActive(item.path) ? '1px solid rgba(254,52,232,0.2)' : '1px solid transparent', transition: 'all 0.15s' }}>
                  {item.label}
                </div>
              ))}
            </nav>
            <div style={{ padding: '10px 8px', borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, paddingBottom: '80px' }}>
              <div onClick={async () => { await logout(); setMobileOpen(false); }} style={{ display: 'flex', alignItems: 'center', padding: '13px 14px', borderRadius: '10px', fontSize: '15px', fontWeight: 600, color: C.danger, cursor: 'pointer' }}>
                🚪 Sign Out
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginBottom: '14px', lineHeight: 1.5 }}>Join Oracle Nexus to find verified global opportunities</p>
              <button onClick={() => { openAuthModal('register'); setMobileOpen(false); }} style={{ width: '100%', padding: '13px', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, border: 'none', borderRadius: '9px', color: '#fff', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: "'Syne',sans-serif", marginBottom: '10px' }}>Get Started →</button>
              <button onClick={() => { openAuthModal('login'); setMobileOpen(false); }} style={{ width: '100%', padding: '12px', background: 'transparent', border: '1px solid rgba(254,52,232,0.3)', borderRadius: '9px', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>Log In</button>
            </div>
            <nav style={{ padding: '10px 8px' }}>
              {navLinks.map(link => (
                <div key={link.path} onClick={() => { handleNavClick(link); setMobileOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 14px', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', color: 'rgba(255,255,255,0.65)', marginBottom: '3px' }}>
                  <span>{link.icon}</span> {link.label}
                </div>
              ))}
            </nav>
          </>
        )}
      </div>

      {authModal && (
        <AuthModal
          mode={authModal}
          onClose={closeAuthModal}
          onSwitch={() => openAuthModal(authModal === 'register' ? 'login' : 'register')}
        />
      )}
    </>
  );
}
